import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Search, ShieldAlert, Compass, Inbox, Users, Coins, X } from "lucide-react";

const GROUP_ICON = {
  risques: ShieldAlert,
  origination: Compass,
  leads: Inbox,
  postes: Users,
  actifs: Coins,
};

export default function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [data, setData] = useState({ groups: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Cmd+K / Ctrl+K
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      } else if (e.key === "Escape" && open) {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
    else { setQ(""); setData({ groups: [], total: 0 }); setActiveIdx(0); }
  }, [open]);

  // search
  useEffect(() => {
    if (!q || q.length < 2) { setData({ groups: [], total: 0 }); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await api.get(`/search?q=${encodeURIComponent(q)}`);
        setData(r.data);
        setActiveIdx(0);
      } catch {
        setData({ groups: [], total: 0 });
      } finally {
        setLoading(false);
      }
    }, 180);
    return () => clearTimeout(t);
  }, [q]);

  const flatItems = data.groups.flatMap((g) => g.items.map((i) => ({ ...i, group: g.key })));

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx((i) => Math.min(i + 1, flatItems.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter" && flatItems[activeIdx]) {
      navigate(flatItems[activeIdx].url); setOpen(false);
    }
  };

  if (!open) return null;
  let runningIdx = -1;

  return (
    <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-start justify-center pt-24 px-4" onClick={() => setOpen(false)} data-testid="cmdk-overlay">
      <div className="w-full max-w-2xl noam-card overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="cmdk-root">
        <div className="flex items-center gap-3 px-4 py-3 border-b border-white/5">
          <Search className="w-4 h-4 text-zinc-500" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Rechercher risques, opportunités, leads, postes critiques…"
            className="flex-1 bg-transparent outline-none text-sm text-white placeholder-zinc-500"
            data-testid="cmdk-input"
          />
          <span className="text-[10px] text-zinc-500 px-2 py-0.5 rounded border border-white/10">esc</span>
          <button onClick={() => setOpen(false)} className="text-zinc-500 hover:text-white"><X className="w-4 h-4" /></button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto">
          {q && q.length >= 2 && data.total === 0 && !loading && (
            <div className="p-8 text-center text-sm text-zinc-500" data-testid="cmdk-empty">Aucun résultat pour « {q} »</div>
          )}
          {(!q || q.length < 2) && (
            <div className="p-8 text-center text-sm text-zinc-500">
              Tapez au moins 2 caractères. <span className="text-zinc-600">·</span>{" "}
              <span className="text-zinc-400">↑↓</span> naviguer · <span className="text-zinc-400">↵</span> ouvrir
            </div>
          )}
          {data.groups.map((g) => {
            const Icon = GROUP_ICON[g.key] || Search;
            return (
              <div key={g.key} className="py-1" data-testid={`cmdk-group-${g.key}`}>
                <div className="px-4 py-1.5 text-[10px] uppercase tracking-[0.18em] text-zinc-500">{g.label}</div>
                {g.items.map((it) => {
                  runningIdx += 1;
                  const isActive = runningIdx === activeIdx;
                  return (
                    <button
                      key={it.id}
                      onMouseEnter={() => setActiveIdx(runningIdx)}
                      onClick={() => { navigate(it.url); setOpen(false); }}
                      className={`w-full text-left px-4 py-2.5 flex items-center gap-3 transition-colors ${isActive ? "bg-[#378ADD]/15" : "hover:bg-white/[0.03]"}`}
                      data-testid={`cmdk-item-${it.id}`}
                    >
                      <Icon className="w-4 h-4 text-zinc-500 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-white truncate">{it.label}</div>
                        <div className="text-[11px] text-zinc-500 truncate">{it.sub}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>

        <div className="px-4 py-2 border-t border-white/5 text-[10px] text-zinc-500 flex items-center gap-3">
          <span>Cmd/Ctrl + K pour ouvrir</span>
          <span className="opacity-30">·</span>
          <span>{loading ? "Recherche…" : `${data.total} résultat${data.total > 1 ? "s" : ""}`}</span>
        </div>
      </div>
    </div>
  );
}
